import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Doctor } from '../../core/models/doctor.model';
import { Router } from '@angular/router';
import { DoctorProfileStatus } from '../../core/enums/doctor-profile-status.enum';
import { BookingService } from '../../core/services/booking.service';
import { DoctorReportSummary } from '../../core/models/doctor-report-summary';
import { NotificationService } from '../../core/services/notification.service';
import { DoctorProfileService } from '../../core/services/doctor-profile.service';
import { MessagingService } from '../../core/services/messaging.service';
import { Conversation } from '../../core/models/conversation.model';
import { ScheduleCalendarView } from '../schedule-calendar-view/schedule-calendar-view';
import { PersonalScheduleComponent } from '../personal-schedule/personal-schedule';
import { PortalNavigation, PortalNavigationItem } from '../portal-navigation/portal-navigation';
import { SpecializationLabelPipe } from '../../core/pipes/specialization-label.pipe';
import { TodoService } from '../../core/services/todo.service';
import { TodoModel } from '../../core/models/todo.model';
import { ClinicStatusService } from '../../core/services/clinic-status.service';
import { ClinicStatus } from '../../core/enums/clinic-status.enum';
import { UserManagementService } from '../../core/services/user-management.service';
import { ClinicMembership } from '../../core/models/clinic-membership.model';
import { SettingsService } from '../../core/services/settings.service';
import { AppearanceSettings } from '../../core/models/appearance-settings.model';
import { PendingClinicInvitationResponse } from '../../core/models/pending-clinic-invitation-response.model';

import { TermsAndConditionsModal } from '../terms-and-conditions-modal/terms-and-conditions-modal';
import { ContactUsModal } from '../contact-us-modal/contact-us-modal';

@Component({
  selector: 'app-doctor-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    ScheduleCalendarView,
    TermsAndConditionsModal,
    ContactUsModal,
    PersonalScheduleComponent,
    PortalNavigation,
    SpecializationLabelPipe,
  ],
  templateUrl: './doctor-dashboard.html',
  styleUrls: ['./doctor-dashboard.scss'],
})
export class DoctorDashboard implements OnInit {
  @ViewChild(ScheduleCalendarView) calendar!: ScheduleCalendarView;

  statusMessage: string = '';
  isOnline: boolean = false;
  doctorData: Doctor | null = null;

  reportSummary: DoctorReportSummary | undefined;

  isLoading = true;
  apiCalls = 4;

  conversations: Conversation[] = [];

  upcomingTodos: TodoModel[] = [];
  todosLoading = false;
  isClinicActive = false;
  clinicStatus: ClinicStatus | null = null;
  clinicMemberships: ClinicMembership[] = [];
  pendingInvitations: PendingClinicInvitationResponse[] = [];
  isLoadingInvitations = false;

  // Pagination properties
  todosCurrentPage = 1;
  todosItemsPerPage = 5;
  todosPageSize: number = 0;
  isLinkedToClinic = false;
  appearanceSettings: AppearanceSettings | undefined;

  stats = {
    completed: 12,
    cancelled: 3,
    hoursWorked: 18,
  };
  doctorId: any;

  get primaryNavigationItems(): PortalNavigationItem[] {
    if (!this.isLinkedToClinic) return [];
    return [
      {
        label: 'Patients',
        icon: 'bi-people-fill',
        route: '/dashboard/patients-view',
        disabled: !this.isClinicActive,
      },
      {
        label: 'Bookings',
        icon: 'bi-calendar-check-fill',
        route: '/dashboard/booking-list',
        disabled: !this.isClinicActive,
      },
      {
        label: 'Create Booking',
        icon: 'bi-calendar-plus-fill',
        route: '/dashboard/create-booking',
        disabled: !this.isClinicActive,
      },
    ];
  }

  constructor(
    public router: Router,
    // private bookingDataStore: DataStoreService,
    private notificationService: NotificationService,
    private profileService: DoctorProfileService,
    private messagingService: MessagingService,
    private todoService: TodoService,
    private clinicStatusService: ClinicStatusService,
    private bookingsService: BookingService,
    private userManagementService: UserManagementService,
    private settingsService: SettingsService,
  ) {
    this.doctorData = history.state['doctorData'];
    this.doctorId = this.doctorData?.id;
  }

  ngOnInit(): void {
    // FIX: Removed the 'throw new Error' statement.
    // An error in ngOnInit stops the component from rendering correctly.
    // Initialization logic would normally go here.

    this.getStatus();
    this.getReport();
    this.getUserConversations();
    this.getUpcomingTodos();
    this.loadClinicMemberships();
    this.loadPendingInvitations();
    this.loadAppearanceSettings();
    this.clinicStatusService.load();

    this.clinicStatusService.isClinicActive$.subscribe((active) => {
      this.isClinicActive = active;
    });
    this.clinicStatusService.status$.subscribe((status) => {
      this.clinicStatus = status;
    });
    this.clinicStatusService.hasClinic$.subscribe((has) => {
      this.isLinkedToClinic = has;
    });

    if (this.doctorData == null) {
      this.profileService.getDoctorProfileDetails().subscribe(
        (res) => {
          if (this.apiCalls > 1) {
            this.apiCalls = this.apiCalls - 1;
          } else if (this.apiCalls === 0) {
            this.isLoading = false;
          }
          this.doctorData = res;
          this.doctorId = this.doctorData?.id;
        },
        (error) => {
          this.isLoading = false;
          this.notificationService.error(error.error.message);
        },
      );
    }
  }

  private loadClinicMemberships(): void {
    this.userManagementService.getClinicMemberships().subscribe({
      next: (memberships) => {
        this.clinicMemberships = memberships;
      },
      error: () => {
        this.clinicMemberships = [];
      },
    });
  }

  private loadPendingInvitations(): void {
    this.isLoadingInvitations = true;
    this.profileService.getPendingClinicInvitations().subscribe({
      next: (invitations) => {
        this.pendingInvitations = invitations || [];
        this.isLoadingInvitations = false;
      },
      error: () => {
        this.pendingInvitations = [];
        this.isLoadingInvitations = false;
      },
    });
  }

  private loadAppearanceSettings(): void {
    this.settingsService.getAppearanceSettings().subscribe({
      next: (settings) => {
        this.appearanceSettings = settings;
      },
      error: () => {
        /* Non-fatal: calendar defaults to month view */
      },
    });
  }

  onSwitchClinic(clinicId: string): void {
    this.profileService.switchClinicContext(clinicId);
  }

  getReport() {
    this.bookingsService.getReportSummary(null).subscribe(
      (res) => {
        this.reportSummary = res;
        if (this.apiCalls > 1) {
          this.apiCalls = this.apiCalls - 1;
        } else if (this.apiCalls === 0) {
          this.isLoading = false;
        }
      },
      (error) => {
        this.notificationService.error(error.error.message);
        this.isLoading = false;
      },
    );
  }

  getUserConversations() {
    this.messagingService.getConversations().subscribe((res) => {
      this.conversations = res;
      if (this.apiCalls > 1) {
        this.apiCalls = this.apiCalls - 1;
      } else if (this.apiCalls === 0) {
        this.isLoading = false;
      }
    });
  }

  getUpcomingTodos(): void {
    this.todosLoading = true;
    this.todoService.getUpcomingTodos().subscribe({
      next: (res) => {
        this.upcomingTodos = res;
        this.todosPageSize = Math.ceil(res.length / this.todosItemsPerPage);
        this.todosCurrentPage = 1; // Reset to first page
        this.todosLoading = false;
      },
      error: (err) => {
        this.todosLoading = false;
        // Non-fatal: silently degrade
      },
    });
  }

  get paginatedTodos(): TodoModel[] {
    const startIndex = (this.todosCurrentPage - 1) * this.todosItemsPerPage;
    const endIndex = startIndex + this.todosItemsPerPage;
    return this.upcomingTodos.slice(startIndex, endIndex);
  }

  todosPreviousPage(): void {
    if (this.todosCurrentPage > 1) {
      this.todosCurrentPage--;
    }
  }

  todosNextPage(): void {
    if (this.todosCurrentPage < this.todosPageSize) {
      this.todosCurrentPage++;
    }
  }

  todosGoToPage(page: number): void {
    if (page >= 1 && page <= this.todosPageSize) {
      this.todosCurrentPage = page;
    }
  }

  onTaskCreated(): void {
    this.getUpcomingTodos();
    // Refresh calendar to show newly created task
    if (this.calendar) {
      this.calendar.getUpcomingTodos();
      this.calendar.getDailyTodos();
    }
  }

  deleteUpcomingTodo(todoId: string): void {
    this.todoService.deleteTodo(todoId).subscribe({
      next: () => {
        this.upcomingTodos = this.upcomingTodos.filter((todo) => todo.id !== todoId);
        this.notificationService.success('Task deleted.');
      },
      error: (error) => {
        this.notificationService.error(error?.error?.message || 'Failed to delete task.');
      },
    });
  }

  bookOffSlots() { }

  getStatus(): void {
    this.profileService.getDoctorOnlineStatus().subscribe(
      (res) => {
        this.isOnline = res;
        this.isLoading = false;

        if (this.apiCalls > 1) {
          this.apiCalls = this.apiCalls - 1;
        } else if (this.apiCalls === 0) {
          this.isLoading = false;
        }
      },
      (error) => {
        this.notificationService.error(error.error.message);
      },
    );
  }

  toggleOnline(): void {
    this.profileService.updateDoctorOnlineStatus(this.isOnline).subscribe(
      (res) => {
        this.isOnline = !this.isOnline;
        this.notificationService.success(
          this.isOnline === true ? 'STATUS ONLINE' : 'STATUS OFFLINE',
        );
      },
      (error) => {
        this.notificationService.error(error.error.message);
      },
    );
  }

  openConversation(chat: Conversation) {
    this.router.navigate(['dashboard/chat-messages'], {
      state: { bookingId: chat.bookingId },
    });
  }

  navigateToPatients() {
    this.router.navigate(['dashboard/patients-view']);
  }

  navigateToBookings() {
    this.router.navigate(['dashboard/booking-list']);
  }

  navigateToCreateBooking() {
    this.router.navigate(['dashboard/create-booking']);
  }

  viewMoreStats() {
    this.router.navigate(['dashboard/stats']); // navigate to your dedicated stats component
  }

  navigateToProfile(): void {
    // this.router.navigate(['dashboard/doctor-profile']);
    this.router.navigate(['/dashboard/profile']);
  }

  navigateToSettings(): void {
    this.router.navigate(['dashboard/settings']);
  }

  protected readonly DoctorProfileStatus = DoctorProfileStatus;

  get clinicStatusDisplay(): { label: string; icon: string; textClass: string; pillClass: string } {
    switch (this.clinicStatus) {
      case ClinicStatus.ACTIVE:
        return {
          label: 'Practice Active',
          icon: 'bi-calendar-check',
          textClass: 'text-success',
          pillClass: 'status-confirmed',
        };
      case ClinicStatus.REVIEW_PENDING:
        return {
          label: 'Review Pending',
          icon: 'bi-clock-history',
          textClass: 'text-warning',
          pillClass: 'status-pending',
        };
      case ClinicStatus.SUSPENDED:
        return {
          label: 'Practice Suspended',
          icon: 'bi-x-octagon',
          textClass: 'text-danger',
          pillClass: 'status-cancelled',
        };
      case ClinicStatus.INACTIVE:
        return {
          label: 'Practice Inactive',
          icon: 'bi-dash-circle',
          textClass: 'text-secondary',
          pillClass: 'status-cancelled',
        };
      default:
        return {
          label: 'Review Pending',
          icon: 'bi-clock-history',
          textClass: 'text-warning',
          pillClass: 'status-pending',
        };
    }
  }
}
